export type ConsentJurisdiction = "EU" | "US_CA" | "OTHER";

export interface CookieConsentPreferences {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  jurisdiction: ConsentJurisdiction;
  version: 1;
  consentedAt: string;
}

export const COOKIE_CONSENT_KEY = "cookie_consent_v2";

// EU member states + EEA + UK (all subject to GDPR / UK GDPR)
const EU_COUNTRY_CODES = new Set([
  "AT","BE","BG","CY","CZ","DE","DK","EE","ES","FI","FR","GR","HR",
  "HU","IE","IT","LT","LU","LV","MT","NL","PL","PT","RO","SE","SI","SK",
  "NO","IS","LI","GB",
]);

export function loadConsent(): CookieConsentPreferences | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsentPreferences;
  } catch {
    return null;
  }
}

export function saveConsent(prefs: CookieConsentPreferences): void {
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
  window.dispatchEvent(new CustomEvent("cookieConsentUpdated", { detail: prefs }));
}

/** Default analytics/marketing toggles based on jurisdiction. */
export function getDefaultsForJurisdiction(
  jurisdiction: ConsentJurisdiction,
): Pick<CookieConsentPreferences, "analytics" | "marketing"> {
  // EU/UK: explicit opt-in required for all non-essential (GDPR)
  // US-CA: analytics default on (opt-out per CCPA), marketing opt-in globally
  // Other: treat same as EU for best-practice
  if (jurisdiction === "US_CA") {
    return { analytics: true, marketing: false };
  }
  return { analytics: false, marketing: false };
}

export async function detectJurisdiction(): Promise<ConsentJurisdiction> {
  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 3000);
    const res = await fetch("https://ip-api.com/json/?fields=countryCode,regionCode", {
      signal: controller.signal,
    });
    clearTimeout(id);
    if (!res.ok) return fallbackJurisdiction();
    const data = (await res.json()) as { countryCode?: string; regionCode?: string };
    const country = data.countryCode ?? "";
    const region = data.regionCode ?? "";
    if (EU_COUNTRY_CODES.has(country)) return "EU";
    if (country === "US" && region === "CA") return "US_CA";
    return "OTHER";
  } catch {
    return fallbackJurisdiction();
  }
}

function fallbackJurisdiction(): ConsentJurisdiction {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz.startsWith("Europe/")) return "EU";
  } catch {
    // ignore
  }
  return "OTHER";
}

/** True when analytics scripts may run. */
export function analyticsAllowed(): boolean {
  return loadConsent()?.analytics === true;
}

/** True when marketing/referral scripts may run. */
export function marketingAllowed(): boolean {
  return loadConsent()?.marketing === true;
}

// ---------------------------------------------------------------------------
// Per-language strings for the cookie consent UI (EN + DA required for launch)
// ---------------------------------------------------------------------------

interface CookieConsentStrings {
  title: string;
  description: string;
  descriptionCA: string; // CCPA variant
  privacyLink: string;
  acceptAll: string;
  rejectAll: string;
  managePreferences: string;
  doNotSell: string;
  savePreferences: string;
  categories: {
    essential: { title: string; description: string };
    analytics: { title: string; description: string };
    marketing: { title: string; description: string };
  };
  alwaysOn: string;
}

export const COOKIE_CONSENT_STRINGS: Record<string, CookieConsentStrings> = {
  en: {
    title: "🍪 Cookie preferences",
    description:
      "We use cookies to keep you signed in (essential) and to understand how you use NordicTime (analytics). Marketing cookies let us track referrals.",
    descriptionCA:
      "We use cookies to keep you signed in. You may opt out of analytics at any time. Marketing cookies are off by default.",
    privacyLink: "Privacy Policy",
    acceptAll: "Accept all",
    rejectAll: "Reject non-essential",
    managePreferences: "Manage preferences",
    doNotSell: "Do not sell my personal information",
    savePreferences: "Save preferences",
    alwaysOn: "Always on",
    categories: {
      essential: {
        title: "Essential",
        description:
          "Required for the site to work: authentication sessions and security tokens. Cannot be disabled.",
      },
      analytics: {
        title: "Analytics",
        description:
          "Help us understand how you use NordicTime so we can improve it. No personal data is sent to third parties.",
      },
      marketing: {
        title: "Marketing",
        description:
          "Used for referral tracking (Rewardful). Enables commission-based referrals when you share your link.",
      },
    },
  },
  da: {
    title: "🍪 Cookie-indstillinger",
    description:
      "Vi bruger cookies til at holde dig logget ind (nødvendige) og til at forstå, hvordan du bruger NordicTime (analyse). Markedsføringscookies bruges til at spore henvisninger.",
    descriptionCA:
      "Vi bruger cookies til at holde dig logget ind. Du kan til enhver tid framelde dig analyse. Markedsføringscookies er slået fra som standard.",
    privacyLink: "Privatlivspolitik",
    acceptAll: "Acceptér alle",
    rejectAll: "Afvis ikke-nødvendige",
    managePreferences: "Administrer indstillinger",
    doNotSell: "Sælg ikke mine personlige oplysninger",
    savePreferences: "Gem indstillinger",
    alwaysOn: "Altid aktiv",
    categories: {
      essential: {
        title: "Nødvendige",
        description:
          "Krævet for at siden virker: loginsessioner og sikkerhedstokens. Kan ikke deaktiveres.",
      },
      analytics: {
        title: "Analyse",
        description:
          "Hjælper os med at forstå, hvordan du bruger NordicTime, så vi kan forbedre det. Ingen personlige data sendes til tredjeparter.",
      },
      marketing: {
        title: "Markedsføring",
        description:
          "Bruges til henvisningssporing (Rewardful). Muliggør provisionsbaserede henvisninger, når du deler dit link.",
      },
    },
  },
};

export function getCookieConsentStrings(lang: string): CookieConsentStrings {
  return COOKIE_CONSENT_STRINGS[lang] ?? COOKIE_CONSENT_STRINGS.en;
}
